let edcCatalogPromise;

function loadEdcProductCatalog() {
  if (edcCatalogPromise) return edcCatalogPromise;
  edcCatalogPromise = (async () => {
    const token = window.__STOREFRONT_TOKEN__;
    const shop = window.Shopify?.shop;
    if (!token || !shop) throw new Error('Product catalog is unavailable');
    const query = `
      query HomepageCompareProducts {
        products(first: 250, sortKey: TITLE) {
          edges {
            node {
              handle
              title
              vendor
              productType
              featuredImage {
                url(transform: { maxWidth: 720, maxHeight: 720, preferredContentType: WEBP })
                altText
                width
                height
              }
              images(first: 3) {
                nodes {
                  url(transform: { maxWidth: 720, maxHeight: 720, preferredContentType: WEBP })
                  altText
                  width
                  height
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 1) {
                nodes { barcode }
              }
              maxOutput: metafield(namespace: "custom", key: "max_output_lumens") { value }
              beamDistance: metafield(namespace: "custom", key: "max_beam_distance_m") { value }
              powerSource: metafield(namespace: "custom", key: "power_source") { value }
              performance: metafield(namespace: "custom", key: "performance_specifications") {
                reference { ... on Metaobject { fields { key value } } }
              }
              build: metafield(namespace: "custom", key: "build_quality") {
                reference { ... on Metaobject { fields { key value } } }
              }
              battery: metafield(namespace: "custom", key: "battery_charging_runtime") {
                reference { ... on Metaobject { fields { key value } } }
              }
              modes: metafield(namespace: "custom", key: "modes_interface") {
                reference { ... on Metaobject { fields { key value } } }
              }
              accessories: metafield(namespace: "custom", key: "accessories_mounting") {
                reference { ... on Metaobject { fields { key value } } }
              }
              special: metafield(namespace: "custom", key: "special_features") {
                reference { ... on Metaobject { fields { key value } } }
              }
              identification: metafield(namespace: "custom", key: "product_identification_warranty_information") {
                reference { ... on Metaobject { fields { key value } } }
              }
            }
          }
        }
      }
    `;
    const response = await fetch(`https://${shop}/api/2025-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    if (!response.ok) throw new Error(`Storefront API returned ${response.status}`);

    const payload = await response.json();
    if (payload.errors?.length) throw new Error(payload.errors[0].message);

    const products = (payload.data?.products?.edges || [])
      .map(({ node }) => node)
      .filter((product) => product.handle && product.title);

    if (!products.length) throw new Error('No published products were returned');

    return products;
  })().catch((error) => {
    edcCatalogPromise = undefined;
    throw error;
  });
  return edcCatalogPromise;
}

class EdcSelectDropdown {
  constructor(select) {
    this.select = select;
    this.activeIndex = 0;
    this.search = '';
    this.searchTime = 0;
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'edc-finder-dropdown';
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'edc-finder-dropdown__trigger';
    this.button.id = `${select.id}-trigger`;
    this.button.setAttribute('role', 'combobox');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.list = document.createElement('div');
    this.list.className = 'edc-finder-dropdown__list';
    this.list.id = `${select.id}-options`;
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;
    this.button.setAttribute('aria-controls', this.list.id);
    const label = select.parentElement.querySelector('label');
    label.id = `${select.id}-label`;
    label.htmlFor = this.button.id;
    this.button.setAttribute('aria-labelledby', `${label.id} ${this.button.id}`);
    this.list.setAttribute('aria-labelledby', label.id);
    this.wrapper.append(this.button, this.list);
    select.after(this.wrapper);
    select.hidden = true;
    this.button.addEventListener('click', () => this.list.hidden ? this.open() : this.close());
    this.button.addEventListener('keydown', (event) => this.onKeyDown(event));
    this.list.addEventListener('pointerdown', (event) => event.preventDefault());
    this.list.addEventListener('click', (event) => {
      const option = event.target.closest('[role="option"]');
      if (option) this.choose(Number(option.dataset.index));
    });
    this.outsideClick = (event) => {
      if (!this.wrapper.contains(event.target)) this.close();
    };
    this.focusOut = (event) => {
      if (!this.wrapper.contains(event.relatedTarget)) this.close();
    };
    this.wrapper.addEventListener('focusout', this.focusOut);
    this.observer = new MutationObserver(() => this.refresh());
    select.addEventListener('change', () => this.refresh());
    this.refresh();
  }

  connect() {
    this.observer.observe(this.select, { childList: true, attributes: true, attributeFilter: ['disabled'] });
    document.addEventListener('pointerdown', this.outsideClick);
    this.refresh();
  }

  disconnect() {
    this.close();
    this.observer.disconnect();
    document.removeEventListener('pointerdown', this.outsideClick);
  }

  refresh() {
    this.button.disabled = this.select.disabled;
    this.button.textContent = this.select.selectedOptions[0]?.textContent || '';
    this.list.replaceChildren(...Array.from(this.select.options).flatMap((option, index) => {
      if (!option.value) return [];
      const item = document.createElement('div');
      item.id = `${this.list.id}-${index}`;
      item.dataset.index = index;
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.textContent = option.textContent;
      return item;
    }));
    this.close();
  }

  open() {
    if (this.button.disabled) return;
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.setActive(Math.max(0, Array.from(this.list.children).findIndex(item => Number(item.dataset.index) === this.select.selectedIndex)));
  }

  close() {
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.button.removeAttribute('aria-activedescendant');
    this.search = '';
  }

  setActive(index) {
    this.activeIndex = Math.max(0, Math.min(index, this.list.children.length - 1));
    Array.from(this.list.children).forEach((item, i) => {
      item.classList.toggle('is-active', i === this.activeIndex);
    });
    const active = this.list.children[this.activeIndex];
    if (active) {
      this.button.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }
  }

  choose(index) {
    this.select.selectedIndex = index;
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
    this.button.focus({ preventScroll: true });
  }

  onKeyDown(event) {
    const open = !this.list.hidden;
    if (event.key === 'Tab') { this.close(); return; }
    if (event.key === 'Escape') {
      if (open) { event.preventDefault(); event.stopPropagation(); this.close(); }
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (!open) { this.open(); return; }
      if (event.key === 'Enter' || event.key === ' ') { this.choose(Number(this.list.children[this.activeIndex].dataset.index)); return; }
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.list.children.length - 1 : this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1);
      this.setActive(index);
    } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (!open) this.open();
      const now = Date.now();
      this.search = (now - this.searchTime < 700 ? this.search : '') + event.key.toLocaleLowerCase();
      this.searchTime = now;
      const index = Array.from(this.list.children).findIndex(option => option.textContent.toLocaleLowerCase().startsWith(this.search));
      if (index >= 0) this.setActive(index);
    }
  }
}

if (!customElements.get('edc-product-comparison')) {
  class EdcProductComparison extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.products = [];
      this.status = this.querySelector('[data-compare-status]');
      this.slots = Array.from(this.querySelectorAll('[data-compare-slot]'));
      this.currency = this.dataset.currency || 'SGD';
      this.locale = this.dataset.locale || 'en';
      this.pickerStates = new Map();
      this.groupAnimations = new Map();
      this.addEventListener('click', this.handleClick.bind(this));
      this.loadProducts();
    }

    disconnectedCallback() {
      this.slots?.forEach((slot) => this.closePicker(slot, false));
      this.groupAnimations?.forEach((state) => {
        state.animations.forEach((animation) => animation.cancel());
        state.rows.forEach((row) => { row.hidden = !state.expanded; });
        state.panel.hidden = !state.expanded;
      });
      this.groupAnimations?.clear();
    }

    async loadProducts() {
      try {
        this.products = await loadEdcProductCatalog();
        this.populateBrands();
        const initialProduct = this.products.find((product) => product.handle === this.dataset.initialProduct);
        if (initialProduct && this.slots[0]) {
          this.renderSlot(this.slots[0], initialProduct);
          this.renderColumn(Number(this.slots[0].dataset.compareSlot), initialProduct);
        }
        this.status.textContent = `${this.products.length} products ready to compare.`;
        this.status.classList.add('visually-hidden');
        window.dispatchEvent(
          new CustomEvent('edc:homepage-products-ready', { detail: { products: this.products } })
        );
      } catch (error) {
        console.error('Homepage product comparison failed to load:', error);
        this.showError('Product comparison is temporarily unavailable.');
        window.dispatchEvent(new CustomEvent('edc:homepage-products-error'));
      }
    }

    populateBrands() {
      const brands = new Map();

      this.products.forEach((product) => {
        const label = product.vendor?.trim();
        if (!label) return;
        const key = label.toLocaleLowerCase();
        if (!brands.has(key)) brands.set(key, label);
      });

      const options = [...brands.entries()].sort((a, b) => a[1].localeCompare(b[1]));
      this.slots.forEach((slot) => {
        const brandSelect = slot.querySelector('[data-compare-brand]');
        brandSelect.replaceChildren(new Option('Choose a brand', ''));
        options.forEach(([value, label]) => brandSelect.add(new Option(label, value)));
        brandSelect.disabled = false;
        slot.querySelector('[data-compare-empty]').disabled = false;
      });
    }

    handleClick(event) {
      const slot = event.target.closest('[data-compare-slot]');
      const addButton = event.target.closest('[data-compare-empty], [data-compare-change]');
      if (addButton) {
        this.pickerStates.set(slot, { step: 'brand', brand: '', type: '', opener: addButton });
        this.renderPicker(slot);
        return;
      }
      if (event.target.closest('[data-picker-close]')) {
        this.closePicker(slot);
        return;
      }
      if (event.target.closest('[data-picker-back]')) {
        const state = this.pickerStates.get(slot);
        state.step = state.step === 'product' ? 'type' : 'brand';
        this.renderPicker(slot, true);
        return;
      }
      const choice = event.target.closest('[data-picker-choice]');
      if (choice) {
        const state = this.pickerStates.get(slot);
        if (state.step === 'product') {
          const product = this.products.find((item) => item.handle === choice.dataset.pickerChoice);
          if (!product) return;
          this.renderSlot(slot, product);
          this.renderColumn(Number(slot.dataset.compareSlot), product);
          this.closePicker(slot, false);
          slot.querySelector('[data-compare-change]').focus({ preventScroll: true });
        } else {
          state[state.step] = choice.dataset.pickerChoice;
          state.step = state.step === 'brand' ? 'type' : 'product';
          this.renderPicker(slot);
        }
        return;
      }
      const groupToggle = event.target.closest('[data-compare-group-toggle]');
      if (groupToggle) {
        this.toggleGroup(groupToggle);
        return;
      }

      const clearButton = event.target.closest('[data-compare-clear]');
      if (clearButton && !clearButton.disabled) this.resetSlot(clearButton.closest('[data-compare-slot]'));
    }

    renderPicker(slot, backwards = false) {
      const state = this.pickerStates.get(slot);
      const picker = slot.querySelector('[data-compare-picker]');
      const heading = document.createElement('h3');
      heading.textContent = `Choose a ${state.step}`;
      heading.tabIndex = -1;
      const controls = document.createElement('div');
      controls.className = 'edc-home-compare__picker-controls';
      controls.append(heading);
      if (state.step !== 'brand') {
        const back = document.createElement('button');
        back.type = 'button';
        back.dataset.pickerBack = '';
        back.textContent = 'BACK';
        controls.append(back);
      }
      const close = document.createElement('button');
      close.type = 'button';
      close.dataset.pickerClose = '';
      close.className = 'edc-home-compare__picker-close';
      close.setAttribute('aria-label', 'Close product picker');
      controls.append(close);
      const list = document.createElement('div');
      list.className = 'edc-home-compare__picker-options';
      let choices;
      if (state.step === 'brand') {
        choices = Array.from(slot.querySelector('[data-compare-brand]').options)
          .filter((option) => option.value).map((option) => [option.value, option.textContent]);
      } else {
        const products = this.products.filter((product) => this.brandKey(product) === state.brand);
        choices = state.step === 'type'
          ? [...new Set(products.map((product) => this.productTypeLabel(product)))].sort().map((type) => [type, type])
          : products.filter((product) => this.productTypeLabel(product) === state.type)
            .sort((a, b) => a.title.localeCompare(b.title)).map((product) => [product.handle, product.title]);
      }
      choices.forEach(([value, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.pickerChoice = value;
        const text = document.createElement('span');
        text.textContent = label;
        button.append(text);
        list.append(button);
      });
      picker.getAnimations().forEach((animation) => animation.cancel());
      picker.replaceChildren(controls, list);
      picker.hidden = false;
      picker.onkeydown = (event) => {
        if (event.key === 'Escape') { event.stopPropagation(); this.closePicker(slot); }
      };
      heading.focus({ preventScroll: true });
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        picker.animate([{ opacity: 0, transform: `translateX(${backwards ? '12' : '-12'}px)` },
          { opacity: 1, transform: 'translateX(0)' }], { duration: 280, easing: 'cubic-bezier(.22,1,.36,1)' });
      }
    }

    closePicker(slot, restoreFocus = true) {
      const state = this.pickerStates.get(slot);
      const picker = slot.querySelector('[data-compare-picker]');
      picker.getAnimations().forEach((animation) => animation.cancel());
      picker.hidden = true;
      this.pickerStates.delete(slot);
      if (restoreFocus) state?.opener.focus({ preventScroll: true });
    }

    toggleGroup(button) {
      const group = button.dataset.compareGroupToggle;
      const expanded = button.getAttribute('aria-expanded') !== 'true';
      const rows = [...this.querySelectorAll(`[data-compare-group-row="${group}"]`)];
      const panel = this.querySelector(`[data-compare-group-panel="${group}"]`);
      const startHeight = panel.getBoundingClientRect().height;
      this.groupAnimations.get(group)?.animations.forEach((animation) => animation.cancel());
      button.setAttribute('aria-expanded', String(expanded));
      rows.forEach((row) => {
        row.setAttribute('aria-hidden', String(!expanded));
        row.hidden = false;
      });
      panel.hidden = false;
      panel.setAttribute('aria-hidden', String(!expanded));
      const endHeight = expanded ? panel.scrollHeight : 0;
      const state = { rows, panel, expanded, animations: [] };
      this.groupAnimations.set(group, state);
      const finish = () => {
        if (this.groupAnimations.get(group) !== state) return;
        rows.forEach((row) => { row.hidden = !expanded; });
        panel.hidden = !expanded;
        state.animations.forEach((animation) => animation.cancel());
        this.groupAnimations.delete(group);
      };
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      const staggerEnd = rows.reduce((latest, row, index) => Math.max(latest,
        Math.floor(index / 2) * 80 + (index % 2) * 45
          + (row.classList.contains('edc-home-compare__value-row') ? (row.cells.length - 1) * 65 : 0)
      ), 0);
      const easing = expanded ? 'cubic-bezier(0.2, 0.7, 0.3, 1)' : 'cubic-bezier(0.7, 0, 0.8, 0.3)';
      rows.forEach((row, index) => {
        const delay = Math.floor(index / 2) * 80 + (index % 2) * 45;
        const targets = row.classList.contains('edc-home-compare__value-row')
          ? [...row.cells]
          : [row];
        targets.forEach((target, column) => {
          state.animations.push(target.animate(
            expanded
              ? [{ opacity: 0, transform: 'translateX(-8px)' }, { opacity: 1, transform: 'translateX(0)' }]
              : [{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: 'translateX(-8px)' }],
            { duration: 240,
              delay: expanded ? delay + column * 65 : staggerEnd - delay - column * 65,
              easing, fill: 'both' }
          ));
        });
      });
      state.animations.push(panel.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: staggerEnd + 240, easing, fill: 'both' }
      ));
      Promise.all(state.animations.map((animation) => animation.finished)).then(finish, () => {});
    }

    renderSlot(slot, product) {
      const image = slot.querySelector('[data-compare-image]');
      const imageLink = slot.querySelector('[data-compare-product-link]');
      const empty = slot.querySelector('[data-compare-empty]');
      const cleanImage = product.handle === 'nitecore-edc09'
        ? product.images?.nodes.find((item) => /\/NitecoreEDC09(?:_\d+x\d+)?\.png(?:\.webp)?$/.test(new URL(item.url).pathname))
        : null;
      const productImage = cleanImage || product.featuredImage;
      image.classList.toggle('edc-home-compare__image--clean', Boolean(cleanImage));

      if (productImage?.url) {
        image.src = productImage.url;
        image.alt = productImage.altText || product.title;
        image.width = productImage.width || 480;
        image.height = productImage.height || 480;
        imageLink.href = `/products/${product.handle}`;
        imageLink.hidden = false;
        imageLink.removeAttribute('tabindex');
        empty.hidden = true;
      } else {
        imageLink.hidden = true;
        empty.hidden = false;
        empty.textContent = 'Image unavailable';
      }

      slot.querySelector('.edc-home-compare__product-heading').hidden = false;
      const title = slot.querySelector('[data-compare-title]');
      title.textContent = product.title;
      title.href = `/products/${product.handle}`;
      title.hidden = false;
      const remove = slot.querySelector('[data-compare-clear]');
      remove.disabled = false;
      remove.hidden = false;
      slot.querySelector('[data-compare-change]').hidden = false;
      this.fadeContent(title);
      image.onload = () => this.fadeContent(image);
      if (image.complete && image.naturalWidth) this.fadeContent(image);
    }

    renderColumn(slotNumber, product) {
      const values = this.productValues(product);
      this.querySelectorAll(`[data-compare-column="${slotNumber}"]`).forEach((cell) => {
        const value = values[cell.dataset.compareValue];
        cell.textContent = value || '—';
        if (cell.closest('.edc-home-compare__quick-row')) this.fadeContent(cell);
      });
    }

    fadeContent(element) {
      element.getAnimations().forEach((animation) => animation.cancel());
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
      }
    }

    productValues(product) {
      const performance = this.metaobjectFields(product.performance);
      const build = this.metaobjectFields(product.build);
      const battery = this.metaobjectFields(product.battery);
      const modes = this.metaobjectFields(product.modes);
      const accessories = this.metaobjectFields(product.accessories);
      const special = this.metaobjectFields(product.special);
      const identification = this.metaobjectFields(product.identification);

      return {
        ...performance,
        ...build,
        ...battery,
        ...modes,
        ...accessories,
        ...special,
        ...identification,
        price: this.formatPrice(product),
        barcode: this.normalizeValue(product.variants?.nodes?.[0]?.barcode) || identification.barcode || '—',
        max_lumens: this.formatMetric(product.maxOutput?.value || performance.max_lumens, 'lm'),
        max_beam_distance: this.formatMetric(
          product.beamDistance?.value || performance.max_beam_distance,
          'm'
        ),
        battery_type: this.normalizeValue(product.powerSource?.value) || battery.battery_type || '—',
        maximum_runtime: battery.maximum_runtime || performance.maximum_runtime || '—',
      };
    }

    metaobjectFields(metafield) {
      return Object.fromEntries(
        (metafield?.reference?.fields || []).map((field) => [field.key, this.normalizeValue(field.value)])
      );
    }

    normalizeValue(value) {
      if (!value) return '';
      const trimmed = String(value).trim();

      if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed.join(', ') : String(parsed);
        } catch (error) {
          return trimmed;
        }
      }

      return trimmed;
    }

    formatMetric(value, unit) {
      const normalized = this.normalizeValue(value);
      if (!normalized) return '—';
      if (/[a-zA-Z]/.test(normalized)) return normalized;

      const numericValue = Number(normalized.replace(/,/g, ''));
      if (!Number.isFinite(numericValue)) return normalized;
      return `${new Intl.NumberFormat(this.locale).format(numericValue)} ${unit}`;
    }

    formatPrice(product) {
      const money = product.priceRange?.minVariantPrice;
      if (!money?.amount) return '—';

      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: money.currencyCode || this.currency,
        }).format(Number(money.amount));
      } catch (error) {
        return `${money.amount} ${money.currencyCode || this.currency}`;
      }
    }

    brandKey(product) {
      return product.vendor?.trim().toLocaleLowerCase() || '';
    }

    productTypeLabel(product) {
      const type = product.productType?.trim() || 'Other';
      const key = type.toLocaleLowerCase().replace(/\s+/g, ' ');

      if (key.includes('headlamp') || key.includes('head lamp')) return 'Headlamps';
      if (key.includes('keychain') || key.includes('flashlight') || key === 'torch') return 'Flashlights';
      if (key.includes('lantern') || key.includes('worklight') || key.includes('work light')) {
        return 'Lanterns & Work Lights';
      }

      return type;
    }

    resetSlot(slot) {
      this.clearSelectedProduct(slot);
      this.closePicker(slot, false);
      slot.querySelector('[data-compare-empty]').focus({ preventScroll: true });
    }

    clearSelectedProduct(slot) {
      const slotNumber = Number(slot.dataset.compareSlot);
      const image = slot.querySelector('[data-compare-image]');
      const imageLink = slot.querySelector('[data-compare-product-link]');
      const empty = slot.querySelector('[data-compare-empty]');

      image.onload = null;
      image.getAnimations().forEach((animation) => animation.cancel());
      image.removeAttribute('src');
      image.alt = '';
      imageLink.href = '#';
      imageLink.hidden = true;
      imageLink.setAttribute('tabindex', '-1');
      const prompt = document.createElement('span');
      prompt.textContent = 'ADD A LIGHT';
      empty.replaceChildren(prompt);
      empty.hidden = false;
      slot.querySelector('.edc-home-compare__product-heading').hidden = true;
      slot.querySelector('[data-compare-change]').hidden = true;
      slot.querySelector('[data-compare-title]').hidden = true;
      slot.querySelector('[data-compare-clear]').disabled = true;
      slot.querySelector('[data-compare-clear]').hidden = true;

      this.querySelectorAll(`[data-compare-column="${slotNumber}"]`).forEach((cell) => {
        cell.textContent = '—';
        cell.getAnimations().forEach((animation) => animation.cancel());
      });
    }

    showError(message) {
      this.status.textContent = message;
      this.status.classList.add('edc-home-compare__status--error');
      this.slots.forEach((slot) => {
        slot.querySelectorAll('select, button').forEach((control) => {
          control.disabled = true;
        });
      });
    }
  }

  customElements.define('edc-product-comparison', EdcProductComparison);
}

if (!customElements.get('edc-product-finder')) {
  class EdcProductFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.dropdowns.forEach((dropdown) => dropdown.connect());
        return;
      }
      this.initialized = true;
      this.products = [];
      this.status = this.querySelector('[data-finder-status]');
      this.brandSelect = this.querySelector('[data-finder-brand]');
      this.typeSelect = this.querySelector('[data-finder-type]');
      this.productSelect = this.querySelector('[data-finder-product]');
      this.productLink = this.querySelector('[data-finder-link]');
      this.dropdowns = [this.brandSelect, this.typeSelect, this.productSelect].map((select) => new EdcSelectDropdown(select));
      this.dropdowns.forEach((dropdown) => dropdown.connect());

      this.addEventListener('change', this.handleChange.bind(this));
      this.productLink.addEventListener('click', (event) => {
        if (this.productLink.getAttribute('aria-disabled') === 'true') event.preventDefault();
      });
    }

    disconnectedCallback() {
      this.dropdowns?.forEach((dropdown) => dropdown.disconnect());
    }

    async ensureProducts() {
      if (this.products.length || this.loading) return;
      this.loading = true;
      this.status.textContent = 'Loading products...';
      this.status.classList.remove('visually-hidden', 'edc-home-product-finder__status--error');
      try {
        this.setProducts(await loadEdcProductCatalog());
      } catch (error) {
        this.showError('Product finder is temporarily unavailable. Close and reopen to retry.');
      } finally {
        this.loading = false;
      }
    }

    setProducts(products) {
      this.products = products.filter((product) => product.handle && product.title);
      if (!this.products.length) {
        this.showError('No products are currently available.');
        return;
      }

      const brands = new Map();
      this.products.forEach((product) => {
        const label = product.vendor?.trim();
        if (!label) return;
        const key = label.toLocaleLowerCase();
        if (!brands.has(key)) brands.set(key, label);
      });

      this.brandSelect.replaceChildren(new Option('Choose a brand', ''));
      [...brands.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .forEach(([value, label]) => this.brandSelect.add(new Option(label, value)));

      this.brandSelect.disabled = false;
      this.status.textContent = `${this.products.length} products available.`;
      this.status.classList.remove('edc-home-product-finder__status--error');
      this.status.classList.add('visually-hidden');
    }

    handleChange(event) {
      if (event.target === this.brandSelect) this.selectBrand();
      if (event.target === this.typeSelect) this.selectType();
      if (event.target === this.productSelect) this.selectProduct();
    }

    selectBrand() {
      const brand = this.brandSelect.value;
      this.typeSelect.replaceChildren(new Option('Choose a type', ''));
      this.productSelect.replaceChildren(new Option('Choose a product', ''));
      this.productSelect.disabled = true;
      this.clearProductLink();

      if (!brand) {
        this.typeSelect.disabled = true;
        return;
      }

      const types = [
        ...new Set(
          this.products
            .filter((product) => this.brandKey(product) === brand)
            .map((product) => this.productTypeLabel(product))
        ),
      ].sort((a, b) => a.localeCompare(b));

      types.forEach((type) => this.typeSelect.add(new Option(type, type)));
      this.typeSelect.disabled = false;
    }

    selectType() {
      const brand = this.brandSelect.value;
      const type = this.typeSelect.value;
      this.productSelect.replaceChildren(new Option('Choose a product', ''));
      this.clearProductLink();

      if (!brand || !type) {
        this.productSelect.disabled = true;
        return;
      }

      this.products
        .filter((product) => this.brandKey(product) === brand && this.productTypeLabel(product) === type)
        .sort((a, b) => a.title.localeCompare(b.title))
        .forEach((product) => this.productSelect.add(new Option(product.title, product.handle)));

      this.productSelect.disabled = false;
    }

    selectProduct() {
      const product = this.products.find((item) => item.handle === this.productSelect.value);
      if (!product) {
        this.clearProductLink();
        return;
      }

      this.productLink.href = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;
      this.productLink.setAttribute('aria-label', `View ${product.title}`);
      this.productLink.setAttribute('aria-disabled', 'false');
      this.productLink.removeAttribute('tabindex');
    }

    clearProductLink() {
      this.productLink.href = '#';
      this.productLink.removeAttribute('aria-label');
      this.productLink.setAttribute('aria-disabled', 'true');
      this.productLink.setAttribute('tabindex', '-1');
    }

    brandKey(product) {
      return product.vendor?.trim().toLocaleLowerCase() || '';
    }

    productTypeLabel(product) {
      const type = product.productType?.trim() || 'Other';
      const key = type.toLocaleLowerCase().replace(/\s+/g, ' ');

      if (key.includes('headlamp') || key.includes('head lamp')) return 'Headlamps';
      if (key.includes('keychain') || key.includes('flashlight') || key === 'torch') return 'Flashlights';
      if (key.includes('lantern') || key.includes('worklight') || key.includes('work light')) {
        return 'Lanterns & Work Lights';
      }

      return type;
    }

    showError(message) {
      this.status.textContent = message;
      this.status.classList.remove('visually-hidden');
      this.status.classList.add('edc-home-product-finder__status--error');
      this.querySelectorAll('select').forEach((select) => {
        select.disabled = true;
      });
      this.clearProductLink();
    }
  }

  customElements.define('edc-product-finder', EdcProductFinder);
}

if (!customElements.get('edc-category-grid')) {
  customElements.define('edc-category-grid', class extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.edc-home__category-card'));
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.motionPreference.matches || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.reveal(entry.target);
        });
      }, { threshold: 0.12 });
      this.cards.forEach((card, index) => {
        if (card.dataset.reveal === 'visible') return;
        card.style.setProperty('--category-reveal-delay', `${index * 120}ms`);
        card.dataset.reveal = 'pending';
        this.observer.observe(card);
      });
      this.handleMotionChange = () => {
        if (this.motionPreference.matches) this.cards.forEach((card) => this.reveal(card));
      };
      this.handleFocus = (event) => {
        const card = event.target.closest('.edc-home__category-card');
        if (card) this.reveal(card);
      };
      this.motionPreference.addEventListener('change', this.handleMotionChange);
      this.addEventListener('focusin', this.handleFocus);
    }

    reveal(card) {
      card.dataset.reveal = 'visible';
      this.observer?.unobserve(card);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.handleMotionChange) this.motionPreference.removeEventListener('change', this.handleMotionChange);
      if (this.handleFocus) this.removeEventListener('focusin', this.handleFocus);
      this.cards?.forEach((card) => { card.dataset.reveal = 'visible'; });
    }
  });
}


function animateEdcDisclosureIndicator(indicator, expanded) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
  return indicator.animate(
    [{ transform: 'rotate(0deg)' }, { transform: `rotate(${expanded ? 180 : -180}deg)` }],
    { duration: 220, easing: 'cubic-bezier(.4, 0, .2, 1)' }
  );
}

if (!customElements.get('edc-faq-disclosure')) {
  customElements.define('edc-faq-disclosure', class extends HTMLElement {
    connectedCallback() {
      this.disclosure = this.querySelector('details');
      this.indicator = this.disclosure.querySelector('summary > .edc-comparison-disclosure__indicator');
      this.expanded = this.disclosure.open;
      this.handleToggle = () => {
        if (this.expanded === this.disclosure.open) return;
        this.expanded = this.disclosure.open;
        this.iconAnimation?.cancel();
        this.iconAnimation = animateEdcDisclosureIndicator(this.indicator, this.expanded);
      };
      this.disclosure.addEventListener('toggle', this.handleToggle);
    }

    disconnectedCallback() {
      this.disclosure?.removeEventListener('toggle', this.handleToggle);
      this.iconAnimation?.cancel();
    }
  });
}

if (!customElements.get('edc-comparison-reveal')) {
  customElements.define('edc-comparison-reveal', class extends HTMLElement {
    connectedCallback() {
      this.disclosure = this.querySelector('details');
      this.trigger = this.disclosure.querySelector('summary');
      this.indicator = this.querySelector('.edc-comparison-disclosure__indicator');
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reveals = [];
      this.targetOpen = this.disclosure.open;
      this.revision = 0;
      this.disclosure.dataset.iconExpanded = String(this.targetOpen);
      this.handleToggle = (event) => {
        event.preventDefault();
        this.targetOpen = !this.targetOpen;
        this.transition(this.targetOpen);
      };
      this.trigger.addEventListener('click', this.handleToggle);
      this.sceneObserver = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-scene-active', entry.isIntersecting && !document.hidden);
      }, { threshold: 0.05 });
      this.sceneObserver.observe(this);
      this.handleVisibility = () => {
        if (document.hidden) {
          this.removeAttribute('data-scene-active');
        } else {
          this.sceneObserver.unobserve(this);
          this.sceneObserver.observe(this);
        }
      };
      document.addEventListener('visibilitychange', this.handleVisibility);
    }

    async transition(opening) {
      const revision = ++this.revision;
      const startHeight = this.disclosure.getBoundingClientRect().height;
      this.disclosure.style.height = `${startHeight}px`;
      this.animation?.cancel();
      this.iconAnimation?.cancel();
      this.reveals.forEach((animation) => animation.cancel());
      this.reveals = [];
      const content = this.querySelector('.edc-comparison-disclosure__content');
      if (this.motionPreference.matches) {
        this.disclosure.open = opening;
        this.disclosure.removeAttribute('data-closing');
        this.disclosure.dataset.iconExpanded = String(opening);
        this.disclosure.style.height = '';
        content.inert = !opening;
        return;
      }
      this.rotateIcon(opening);
      let endHeight;
      if (opening) {
        this.disclosure.open = true;
        this.disclosure.removeAttribute('data-closing');
        content.inert = false;
        this.disclosure.style.height = '';
        endHeight = this.disclosure.getBoundingClientRect().height;
      } else {
        this.disclosure.setAttribute('data-closing', '');
        content.inert = true;
        endHeight = parseFloat(getComputedStyle(this.querySelector('.edc-comparison-disclosure__art')).height);
      }
      this.disclosure.style.height = `${startHeight}px`;
      this.animation = this.disclosure.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 560, fill: 'forwards', easing: 'cubic-bezier(.4, 0, .2, 1)' }
      );
      if (opening) {
        const items = this.querySelectorAll('.edc-home__compare-header, .edc-home-compare__card, .edc-home-compare__details-scroll');
        this.reveals = Array.from(items, (item, index) => item.animate(
          [{ opacity: 0, transform: 'translateY(24px)' }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 320, delay: 80 + index * 40, fill: 'backwards', easing: 'cubic-bezier(.4, 0, .2, 1)' }
        ));
      }
      try {
        await this.animation.finished;
      } catch {
        return;
      }
      if (revision !== this.revision) return;
      this.disclosure.open = opening;
      this.disclosure.removeAttribute('data-closing');
      this.disclosure.style.height = '';
      this.animation.cancel();
      this.animation = null;
    }

    async rotateIcon(expanded) {
      this.disclosure.dataset.iconExpanded = String(expanded);
      this.iconAnimation = animateEdcDisclosureIndicator(this.indicator, expanded);
      try {
        await this.iconAnimation?.finished;
      } catch {
        return;
      }
    }

    disconnectedCallback() {
      this.revision += 1;
      this.sceneObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.trigger?.removeEventListener('click', this.handleToggle);
      this.animation?.cancel();
      this.iconAnimation?.cancel();
      this.reveals?.forEach((animation) => animation.cancel());
      if (this.disclosure) {
        this.disclosure.style.height = '';
        this.disclosure.removeAttribute('data-closing');
      }
    }
  });
}
